import dynamic from 'next/dynamic';

import PanelLoadingFallback from './PanelLoadingFallback';

import type { ComponentType } from 'react';

type AnyPanelComponent = ComponentType<any>;

type LazyPanelLoader<Props extends object> = () => Promise<{
  default: ComponentType<Props>;
}>;

/**
 * 모듈에서 컴포넌트 타입인 export 이름만 추립니다.
 */
type PanelExportName<Module> = {
  [Key in keyof Module]: Module[Key] extends AnyPanelComponent ? Key : never;
}[keyof Module];

/**
 * 모듈의 지정 export가 받는 props 타입을 꺼냅니다.
 */
type PanelExportProps<Module, ExportName extends keyof Module> =
  Module[ExportName] extends ComponentType<infer Props> ? Props : never;

/**
 * Panel 구현을 최초 렌더링 시점까지 지연하는 공통 factory입니다.
 *
 * @description
 * - Panel Scope와 Provider는 동기로 유지하고 실제 Panel 구현만 dynamic chunk로 분리합니다.
 * - Panel은 브라우저 상호작용 이후 렌더링되므로 SSR을 비활성화합니다.
 * - loader는 webpack이 import 경로를 정적으로 분석할 수 있도록 리터럴 import 함수를 받습니다.
 * - default export 파일은 loader만 넘기고, slice barrel(`index.ts`)의 named export는
 *   두 번째 인자로 export 이름을 넘깁니다. 컴포넌트가 아닌 export 이름은 타입 에러입니다.
 *
 * @example
 * // default export 파일
 * const UserPanel = createLazyPanel(() => import('@remotes/components/user/panel/UserPanel'));
 *
 * // FSD slice barrel의 named export
 * const ClusterPanel = createLazyPanel(() => import('@remotes/widgets/cluster-detail'), 'ClusterPanel');
 */
export function createLazyPanel<Props extends object>(
  loader: LazyPanelLoader<Props>,
): ComponentType<Props>;
export function createLazyPanel<Module, ExportName extends PanelExportName<Module>>(
  loader: () => Promise<Module>,
  exportName: ExportName,
): ComponentType<PanelExportProps<Module, ExportName>>;
export function createLazyPanel(
  loader: () => Promise<Record<PropertyKey, unknown>>,
  exportName?: PropertyKey,
): AnyPanelComponent {
  // next/dynamic 옵션은 SWC 변환 대상이라 상수로 빼지 않고 호출마다 객체 리터럴로 넘긴다.
  const resolvedLoader =
    exportName === undefined
      ? (loader as LazyPanelLoader<object>)
      : () => loader().then(module => ({ default: module[exportName] as AnyPanelComponent }));

  return dynamic(resolvedLoader, {
    loading: () => <PanelLoadingFallback />,
    ssr: false,
  });
}
